// =============================================
// PÁGINA DE RELATÓRIO DE PRODUTIVIDADE DE EQUIPAMENTOS
// =============================================
// Arquivo: EquipmentProductivity.tsx
// Descrição: Relatório detalhado de produtividade de equipamentos
// =============================================

import React, { useState } from 'react';
import { apiService } from '../../services/api';
import FilterPanel from '../../components/Common/FilterPanel';
import ReportActions from '../../components/Reports/ReportActions';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { getToken, Config } from '../../services/config';
import { printReport } from '../../utils/printReport';
import './Reports.css';

// =============================================
// INTERFACES
// =============================================

interface EquipmentData {
  id: string;
  nome: string;
  tipo: string;
  tag: string;
  localizacao: string;
  status: string;
  total_checklists: number;
  checklists_concluidos: number;
  checklists_reprovados: number;
  total_ocorrencias: number;
  ocorrencias_resolvidas: number;
  horas_trabalhadas: number;
  ultima_atividade: string;
  taxa_aprovacao: number;
  taxa_resolucao_ocorrencias: number;
  produtividade_score: number;
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

const EquipmentProductivity: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [estatisticas, setEstatisticas] = useState<any>(null);
  const [filters, setFilters] = useState<Record<string, any>>({
    dataInicio: '',
    dataFim: '',
    equipamentoId: '',
    tipo: ''
  });

  // =============================================
  // HOOK DE INFINITE SCROLL
  // =============================================

  const {
    items: equipmentData,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore,
    reset,
    loadAll
  } = useInfiniteScroll<EquipmentData>({
    fetchFunction: async (page, limit, appliedFilters) => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());

        Object.entries(appliedFilters || {}).forEach(([key, value]) => {
          if (value) {
            queryParams.append(key, String(value));
          }
        });

        const response = await apiService.getCustom<any>(
          `/relatorios/produtividade-equipamentos?${queryParams.toString()}`
        );

        if (response.success && response.data) {
          // Armazenar estatísticas na primeira carga
          if (page === 1 && response.data.estatisticas) {
            setEstatisticas(response.data.estatisticas);
          }

          return {
            success: true,
            data: {
              items: response.data.relatorio || [],
              pagination: {
                total: response.data.pagination?.total || response.data.relatorio?.length || 0
              }
            }
          };
        }

        throw new Error('Erro ao carregar relatório');
      } catch (err: any) {
        console.error('Erro ao buscar relatório:', err);
        setError(err.message || 'Erro ao carregar relatório');
        return {
          success: false,
          data: [],
          message: err.message || 'Erro ao carregar relatório'
        };
      }
    },
    itemsPerPage: 10,
    filters: filters
  });

  // =============================================
  // MANIPULADORES DE EVENTOS
  // =============================================

  const handleExportPDF = async () => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, String(value));
        }
      });

      const token = getToken();

      if (!token) {
        alert('Sessão expirada. Faça login novamente.');
        return;
      }

      const url = `${Config.API_BASE_URL}/relatorios/exportar/produtividade-equipamentos/pdf?${queryParams.toString()}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `relatorio-produtividade-equipamentos-${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || 'Erro ao exportar PDF. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
      alert('Erro ao exportar PDF. Verifique sua conexão.');
    }
  };

  const handleExportCSV = async () => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, String(value));
        }
      });

      const token = getToken();

      if (!token) {
        alert('Sessão expirada. Faça login novamente.');
        return;
      }

      const url = `${Config.API_BASE_URL}/relatorios/exportar/produtividade-equipamentos/csv?${queryParams.toString()}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `relatorio-produtividade-equipamentos-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || 'Erro ao exportar CSV. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao exportar CSV:', err);
      alert('Erro ao exportar CSV. Verifique sua conexão.');
    }
  };


  // =============================================
  // RENDERIZAÇÃO DE COMPONENTES
  // =============================================

  const renderStatistics = () => {
    if (!estatisticas) return null;

    return (
      <div className="report-statistics">
        <div className="stat-card">
          <div className="stat-icon stat-icon-primary">
            <i className="fas fa-tools"></i>
          </div>
          <div className="stat-content">
            <h4>Total de Equipamentos</h4>
            <p className="stat-value">{estatisticas.total_equipamentos}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-success">
            <i className="fas fa-clipboard-check"></i>
          </div>
          <div className="stat-content">
            <h4>Total de Checklists</h4>
            <p className="stat-value">{estatisticas.total_checklists ?? 0}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-warning">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div className="stat-content">
            <h4>Total de Ocorrências</h4>
            <p className="stat-value">{estatisticas.total_ocorrencias ?? 0}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-info">
            <i className="fas fa-percentage"></i>
          </div>
          <div className="stat-content">
            <h4>Taxa Média de Aprovação</h4>
            <p className="stat-value">{estatisticas.media_taxa_aprovacao ?? 0}%</p>
          </div>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    if (!equipmentData || equipmentData.length === 0) {
      return (
        <div className="no-data">
          <i className="fas fa-inbox"></i>
          <p>Nenhum dado encontrado para os filtros selecionados</p>
        </div>
      );
    }

    return (
      <div className="report-table-container">
        {/* Contador de resultados */}
        <ResultsCounter
          displayedItems={displayedItems}
          totalItems={totalItems}
          isLoading={isLoading}
          label="equipamento(s)"
        />

        <table className="report-table">
          <thead>
            <tr>
              <th>Equipamento</th>
              <th>Tipo</th>
              <th>TAG</th>
              <th>Localização</th>
              <th>Status</th>
              <th>Checklists</th>
              <th>Taxa Aprovação</th>
              <th>Ocorrências</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {equipmentData.map((item) => (
              <tr key={item.id}>
                <td className="font-weight-bold">{item.nome}</td>
                <td>{item.tipo}</td>
                <td>
                  <span className="badge badge-secondary">{item.tag}</span>
                </td>
                <td>{item.localizacao || '-'}</td>
                <td>
                  <span className={`badge badge-${getStatusColor(item.status)}`}>
                    {item.status}
                  </span>
                </td>
                <td>
                  <span className="text-muted">{item.total_checklists ?? 0}</span>
                  <small className="text-success"> ({item.checklists_concluidos ?? 0} ✓)</small>
                </td>
                <td>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${item.taxa_aprovacao ?? 0}%` }}
                    ></div>
                    <span className="progress-bar-text">{item.taxa_aprovacao ?? 0}%</span>
                  </div>
                </td>
                <td>
                  {item.total_ocorrencias ?? 0}
                  <small className="text-muted"> ({item.ocorrencias_resolvidas ?? 0} resolvidas)</small>
                </td>
                <td>
                  <span className={`score-badge score-${getScoreLevel(item.produtividade_score ?? 0)}`}>
                    {item.produtividade_score ?? 0}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Botão "Ver Mais" */}
        <LoadMoreButton
          onClick={loadMore}
          isLoading={isLoading}
          hasMore={hasMore}
        />
      </div>
    );
  };

  // =============================================
  // FUNÇÕES AUXILIARES
  // =============================================

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      'ativo': 'success',
      'inativo': 'secondary',
      'manutencao': 'warning'
    };
    return colors[status] || 'secondary';
  };

  const getScoreLevel = (score: number): string => {
    if (score >= 80) return 'high';
    if (score >= 60) return 'medium';
    return 'low';
  };

  const handlePrint = async () => {
    // Carrega TODOS os dados antes de imprimir
    await loadAll();
    // Aguarda um pouco para garantir que o DOM foi atualizado
    setTimeout(() => {
      printReport();
    }, 100);
  };

  // =============================================
  // RENDERIZAÇÃO PRINCIPAL
  // =============================================

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-left">
          <h1>
            <i className="fas fa-tachometer-alt"></i>
            Relatório de Produtividade de Equipamentos
          </h1>
          <p className="report-subtitle">
            Análise detalhada da produtividade e desempenho dos equipamentos
          </p>
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Filtros e Ações</h2>
            <FilterPanel
              fields={[
                {
                  type: 'date',
                  label: 'Data Início',
                  value: filters.dataInicio,
                  onChange: (value) => setFilters(prev => ({ ...prev, dataInicio: value }))
                },
                {
                  type: 'date',
                  label: 'Data Fim',
                  value: filters.dataFim,
                  onChange: (value) => setFilters(prev => ({ ...prev, dataFim: value }))
                },
                {
                  type: 'select',
                  label: 'Tipo de Equipamento',
                  value: filters.tipo,
                  onChange: (value) => setFilters(prev => ({ ...prev, tipo: value })),
                  options: [
                    { value: 'escavadeira', label: 'Escavadeira' },
                    { value: 'retroescavadeira', label: 'Retroescavadeira' },
                    { value: 'trator', label: 'Trator' },
                    { value: 'caminhao', label: 'Caminhão' },
                    { value: 'outros', label: 'Outros' }
                  ]
                }
              ]}
              onFilter={reset}
              onClear={() => {
                setFilters({
                  dataInicio: '',
                  dataFim: '',
                  equipamentoId: '',
                  tipo: ''
                });
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Relatório"
            />
          </div>

          <div className="card-body">
            {error && (
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            {!isLoading && equipmentData && equipmentData.length > 0 && (
              <>
                <ReportActions
                  onExportPDF={handleExportPDF}
                  onExportCSV={handleExportCSV}
                  onPrint={handlePrint}
                  disabled={!equipmentData || equipmentData.length === 0}
                />

                {renderStatistics()}

                <div className="report-content">
                  <div className="report-table-header">
                    <h3>Dados Detalhados</h3>
                  </div>
                  {renderTable()}
                </div>
              </>
            )}

            {isLoading && equipmentData.length === 0 && (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Carregando relatório...</p>
              </div>
            )}

            {!isLoading && equipmentData.length === 0 && (
              <div className="no-data">
                <i className="fas fa-inbox"></i>
                <p>Nenhum dado encontrado para os filtros selecionados</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentProductivity;
